import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CopyButtonProps {
  /** The text put on the clipboard. */
  value: string;
  /** What is copied, for the tooltip and screen readers ("phone number"). */
  label: string;
  /** "hover" shows it only while its `group/copy` parent is hovered: for
   *  table cells, where one icon per row would be noise. */
  reveal?: "always" | "hover";
  className?: string;
}

/**
 * The small copy button beside a phone number or an email (owner,
 * 2026-10-26: "un petit bouton de type copier-coller discret sur les
 * numéros de téléphone et email (sur les tâches, sociétés et contacts)").
 * It never opens the row or follows the link it sits next to.
 */
export function CopyButton({
  value,
  label,
  reveal = "always",
  className,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy() {
    if (!(await writeToClipboard(value))) {
      toast.error(`Couldn't copy the ${label}`);
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void copy();
      }}
      // Enter or Space here must not reach a clickable row around it.
      onKeyDown={(event) => event.stopPropagation()}
      title={copied ? "Copied" : `Copy the ${label}`}
      aria-label={copied ? `The ${label} is copied` : `Copy the ${label}`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded p-1 transition-colors",
        copied
          ? "text-emerald-500"
          : "text-slate-300 hover:bg-slate-100 hover:text-slate-600",
        reveal === "hover" &&
          !copied &&
          "opacity-0 focus-visible:opacity-100 group-hover/copy:opacity-100 [@media(hover:none)]:opacity-100",
        className,
      )}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

async function writeToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // No clipboard API or no permission: copy through a selection.
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      area.remove();
    }
  }
}
